import { MindsetDto } from "@gap/backend/app-services/mindset/models/MindsetDto"
import { namingConfig, namingConfigLowerCase } from "@gap/utils/config/naming-config"
import { apiClient } from "api/trpc/trpc-client"
import { GapAutocompleteOption } from "features/gap-components/autocomplete/types/GapAutocompleteTypes"
import { makeAutoObservable } from "mobx"
import Loader from "reusable/mobx-helpers/Loader"
import ModalState from "reusable/mobx-helpers/ModalState"
import { formHandler, FormHandlerConfig } from "reusable/mobx-store-utils/form-handlers/new/form-handler"
import { createStoreContext } from "reusable/mobx-store-utils/storeUtils"
import { snackbarSuccess } from "reusable/mui/snackbar/snackbar"
import { enumToAutoCompleteOption } from "features/gap-components/autocomplete/single/GapAutocompleteSingle"
import { MindsetCategoryEnums, MindsetCategoryIconEnum } from "@gap/backend/app-services/mindset/models/MindsetCategoryEnum"
import OrgGuardStore from "features/main/main-router/org-guard/OrgGuardStore"
import { decodeFile } from "reusable/utils/file-operations"
import { messageConfirm } from "reusable/mui/modals/message-confirm"

export default class MindsetsDialogStore {
   fields: MindsetDto.add = {
      name: '',
      description: '',
      categoryIcon: 'business growth',
      categories: [],
      imageFileName: '',
      videoUrl: undefined
   }
   loader = new Loader(false)
   fileUpload: { files: File[], progressPercent: number } = {
      files: [],
      progressPercent: 0
   }
   sp
   private _mindset: Partial<MindsetDto.add> = {}

   constructor(sp: { modalState: ModalState, orgGuardStore: OrgGuardStore }) {
      makeAutoObservable(this)
      this.sp = sp
   }

   get formHandler() {
      return formHandler(this.fields, {
         name: { type: 'string' },
         description: { type: 'string' },
         categoryIcon: { type: 'string' },
         categories: { type: 'array' },
         imageFileName: { type: 'string' },
         videoUrl: { type: 'string' },
      } satisfies FormHandlerConfig)
   }

   get categoryOptions(): GapAutocompleteOption[] {
      return enumToAutoCompleteOption(MindsetCategoryEnums)
   }

   get mindset() {
      return this._mindset
   }

   set mindset(mindset: Partial<MindsetDto.entity>) {
      this._mindset = mindset
      this.fields = {
         name: mindset.name || '',
         description: mindset.description || '',
         categoryIcon: mindset.categoryIcon || 'business growth',
         categories: mindset.categories ?? [],
         imageFileName: mindset.imageFileName || '',
         videoUrl: mindset.videoUrl
      }
      this.fileUpload = {
         files: [],
         progressPercent: 0
      }
      if (mindset.imageFileName) {
         const fileInfo = decodeFile(mindset.imageFileName).originalFile
         this.fileUpload.files = [fileInfo]
      }
   }

   setCategoryIcon = (categoryIcon: MindsetCategoryIconEnum) => {
      this.fields.categoryIcon = categoryIcon
   }

   getMindsetImageUploadUrl = async (file: File) => {
      const result = await apiClient.mindset.getMindsetImageUploadUrl.query({ orgId: this.sp.orgGuardStore.userOrg.orgId, fileName: file.name })
      return result.url
   }

   onFileValidated = ([file]: File[]) => {
      this.fields.imageFileName = file.name
   }

   onFileRemove = () => {
      this.fields.imageFileName = ''
      this.fileUpload = {
         files: [],
         progressPercent: 0
      }
   }

   add = async () => {
      await this.loader.executeLoading(async () => {
         if (!this.fields.imageFileName.length) {
            if (!(await messageConfirm('Are you sure you want to add this with no image attached?'))) {
               return
            }
         }
         if (this.formHandler.validate(MindsetDto.addSchema)) {
            this._mindset = {
               name: this.fields.name,
               description: this.fields.description,
               categoryIcon: this.fields.categoryIcon,
               categories: this.fields.categories,
               imageFileName: this.fields.imageFileName,
               videoUrl: this.fields.videoUrl
            }
            await apiClient.mindset.add.mutate(this.mindset as MindsetDto.add)
            snackbarSuccess(`${namingConfig.mindset.singular} added`)
            this.sp.modalState.closeWithReason('succeeded')
         }
      })
   }

   delete = async () => {
      return await this.loader.executeLoading(async () => {
         if (!(await messageConfirm(`Are you sure you want to delete this ${namingConfigLowerCase.mindset.singular}?`))) {
            return false
         }
         this.sp.modalState.closeWithReason('succeeded')
         await apiClient.mindset.delete.mutate({
            orgId: this.sp.orgGuardStore.userOrg.orgId,
            id: this.mindset.id!
         })
         snackbarSuccess(`${namingConfig.mindset.singular} deleted`)
         return true
      })
   }

   saveChanges = async () => {
      await this.loader.executeLoading(async () => {
         if (!this.fields.imageFileName.length) {
            if (!(await messageConfirm('Are you sure you want to save the changes with no image attached?'))) {
               return
            }
         }
         if (this.formHandler.validate(MindsetDto.editSchema)) {
            this._mindset = {
               ...this._mindset,
               name: this.fields.name,
               description: this.fields.description,
               categoryIcon: this.fields.categoryIcon,
               categories: this.fields.categories,
               imageFileName: this.fields.imageFileName,
               videoUrl: this.fields.videoUrl
            }
            await apiClient.mindset.edit.mutate(this.mindset as MindsetDto.edit)
            snackbarSuccess(`${namingConfig.mindset.singular} updated`)
            this.sp.modalState.closeWithReason('succeeded')
         }
      })
   }
}

export const { Provider: MindsetsDialogStoreProvider, useProvidedStore: useMindsetsDialogStore } = createStoreContext(MindsetsDialogStore)
